/**
 * Token-shaped patterns we strip from any string before it leaves the
 * process — clipboard payloads ("Copy details for support"), Settings →
 * Diagnostics dumps, anywhere user-pasteable text is generated.
 *
 * Captures Anthropic API keys (`sk-ant-...`), GitHub tokens
 * (`ghp_/gho_/ghs_/ghu_/ghr_/github_pat_...`), npm access tokens
 * (`npm_...`), AWS access-key IDs (AKIA/ASIA/AROA/AIDA + 16
 * alphanumerics), and `Bearer <chunk>` headers. Each captured run
 * becomes `[REDACTED-<KIND>]`.
 *
 * Intentionally narrow — false positives on the user's own data
 * cost more than a missed token would here, because the renderer's
 * `safeOnChunk` ALSO redacts the active Anthropic key by string match
 * before chunks leave the main process. This is the second-line
 * sweep against tokens that landed in error messages, log lines, or
 * sub-process output without going through the per-call redaction.
 *
 * Originally lived in `renderer/components/CrucibleErrorState.tsx`;
 * extracted to `shared/` so `main/diagnostics.ts` can scrub the
 * Settings → Diagnostics log tail with the exact same rules instead
 * of forking the patterns.
 */
export function redactSecrets(input) {
    if (!input)
        return input;
    let out = input;
    // Anthropic API keys. The actual token is base64url + ~60+ chars.
    out = out.replace(/sk-ant-[A-Za-z0-9_-]{20,}/g, '[REDACTED-ANTHROPIC-KEY]');
    // GitHub tokens (PAT classic, fine-grained, OAuth, etc.)
    out = out.replace(/\b(?:ghp|gho|ghs|ghu|ghr)_[A-Za-z0-9]{20,}/g, '[REDACTED-GITHUB-TOKEN]');
    out = out.replace(/\bgithub_pat_[A-Za-z0-9_]{20,}/g, '[REDACTED-GITHUB-TOKEN]');
    // npm access tokens.
    out = out.replace(/\bnpm_[A-Za-z0-9]{20,}/g, '[REDACTED-NPM-TOKEN]');
    // AWS access key IDs.
    out = out.replace(/\b(?:AKIA|ASIA|AROA|AIDA)[A-Z0-9]{16}\b/g, '[REDACTED-AWS-KEY]');
    // Bearer tokens captured in headers / log lines.
    out = out.replace(/\bBearer\s+[A-Za-z0-9._\-+/=]{20,}/gi, 'Bearer [REDACTED-BEARER]');
    return out;
}
/**
 * Replace the per-user component of common home-directory paths so
 * diagnostics output doesn't leak the operator's username. Targets:
 *
 *   - macOS:   `/Users/<name>/...`         → `/Users/<USER>/...`
 *   - Linux:   `/home/<name>/...`          → `/home/<USER>/...`
 *   - Windows: `C:\Users\<name>\...`       → `C:\Users\<USER>\...`
 *               `\\?\C:\Users\<name>\...`  → `\\?\C:\Users\<USER>\...`
 *               `C:/Users/<name>/...`     → `C:/Users/<USER>/...`
 *
 * Cross-platform: a Mac-built diagnostic blob can be pasted into a
 * Slack channel with a Windows reader, so we scrub all three shapes
 * regardless of where the process is running. The replacement keeps
 * the rest of the path (everything after the username) intact so the
 * diagnostic still shows _where_ a tool resolved — `/Users/<USER>/.nvm/versions/node/...`
 * is still a useful signal that node came from nvm even after redaction.
 *
 * Codex Round-1 BUG 3: Node frequently normalises Windows paths to
 * forward slashes (`C:/Users/...`) when they cross JSON / IPC
 * boundaries — VS Code, electron-log's file transport, and the
 * `path.posix.*` helpers all do this. The Windows pattern below
 * accepts BOTH separators in EITHER position, so all four shapes
 * (back\back, back/fwd, fwd\back, fwd/fwd) are caught.
 *
 * The POSIX patterns are also tightened to refuse a leading
 * backslash before `/Users` or `/home` (would only happen via
 * cross-platform string mangling) by anchoring with a non-letter
 * lookbehind that excludes `\` — see the comment beside each regex.
 *
 * Issue #309: the system-tools section can leak `/Users/<name>` from
 * a `which pnpm` output (e.g. `/Users/max/.nvm/versions/node/v18.0.0/bin/pnpm`).
 * Scrubbing here keeps Max's username out of the support paste.
 */
export function redactHomePaths(input) {
    if (!input)
        return input;
    let out = input;
    // POSIX `/Users/<name>` (macOS). The username segment is anything
    // up to the next path separator (`/` OR `\` — defensive against
    // cross-platform string mangling), whitespace, or end of string.
    out = out.replace(/(\/Users\/)([^/\s\\]+)/g, '$1<USER>');
    // POSIX `/home/<name>` (Linux). Same separator-or-whitespace
    // anchor. The username component allows backslashes-in-the-name
    // to be rejected the same way as `/Users` above so a Windows-shape
    // path that happens to contain `/home/` substring (rare) doesn't
    // gobble across `\` boundaries.
    out = out.replace(/(\/home\/)([^/\s\\]+)/g, '$1<USER>');
    // Windows `C:\Users\<name>`, `C:/Users/<name>`, and mixed-separator
    // variants. Codex Round-1 BUG 3: forward-slash normalisation is
    // common when paths cross JSON / IPC boundaries on Windows — Node
    // returns `C:/Users/...` from `path.posix.*` helpers and many
    // electron-log file transports do the same. Allow EITHER separator
    // (forward OR back) in BOTH positions; capture the username
    // segment as anything up to the next separator (either kind) or
    // whitespace. Case-insensitive (`c:` vs `C:` both valid on
    // Windows). The optional `\\?\` long-path prefix is matched
    // implicitly because the regex isn't anchored at start.
    out = out.replace(/([A-Za-z]:[\\/]Users[\\/])([^\\/\s]+)/gi, '$1<USER>');
    return out;
}
/**
 * Strip the `user[:password]@` userinfo segment from any `https://`
 * URL so PAT-bearing remotes cannot land in a log file the user might
 * include in a bug report. Covers `x-access-token:pat@`, plain `pat@`,
 * `user:pat@`, and any other RFC 3986 userinfo shape (`[^@\s/]+@`).
 *
 * Defence-in-depth: callers that hold a specific PAT may still want to
 * scrub the literal token bytes too (the helpers in
 * `main/hero-image-commit.ts` and `main/ipc-surfaces/game-generate-hero.ts`
 * both compose this with an exact-token replace). This helper covers
 * the URL-userinfo shape that's hard to enumerate up-front — sibling
 * remotes in `.git/config`, fallback URLs surfaced by git plumbing,
 * etc. — without needing the literal token in scope.
 *
 * Regex notes:
 *  - Case-insensitive (`/i`) so an uppercase `HTTPS://` scheme — valid
 *    per RFC 3986 §3.1 (schemes are case-insensitive) — doesn't slip
 *    through. Codex #916 round-3 BLOCKING.
 *  - Userinfo character class excludes `/` so a path-segment
 *    `@` like `https://host/path@rev` is NOT mis-redacted. Codex
 *    #916 round-3 NIT.
 *  - Capture group `(https://)` preserves the original scheme casing
 *    in the replacement so a log message that originally read
 *    `HTTPS://…` stays consistent post-scrub.
 *
 * Originally lived as a private function in
 * `main/ipc-surfaces/game-generate-hero.ts` (PR #916). Moved here in
 * the #929 follow-up so `main/hero-image-commit.ts` can compose the
 * same regex into its `redactToken` helper instead of carrying a
 * narrower duplicate.
 */
export function redactCredentialedUrls(input) {
    return input.replace(/(https:\/\/)[^@\s\/]+@/gi, '$1***@');
}
/**
 * Composite scrub for any string headed for a user-pasteable
 * diagnostic surface. Runs the token sweep AND the home-path sweep —
 * the system-tools section in `collectDiagnostics` calls this so a
 * single function captures the full "before it leaves the process"
 * contract.
 *
 * The log-tail caller in `diagnostics.ts` continues to use the bare
 * `redactSecrets` — home-path redaction there would invalidate
 * existing test pins on `/Users/test/Library/Logs/Foundry`, and the
 * log tail never contained tokens anyway. Callers opt in by reaching
 * for this function explicitly.
 */
export function redactDiagnostic(input) {
    return redactHomePaths(redactSecrets(input));
}
//# sourceMappingURL=redact-secrets.js.map